<template>
  <v-dialog
    :model-value="modelValue"
    max-width="440"
    persistent
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="deduction-fix-modal">
      <header class="deduction-fix-modal__head">
        <h3 class="deduction-fix-modal__title">{{ title }}</h3>
        <button
          type="button"
          class="deduction-fix-modal__close"
          aria-label="닫기"
          @click="emit('cancel')"
        >
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </header>

      <div class="deduction-fix-modal__body">
        <div
          v-if="hasCurrentAmount && currentAmount !== autoAmount"
          class="deduction-fix-modal__current"
        >
          <span class="deduction-fix-modal__current-label">현재 입력값</span>
          <span class="deduction-fix-modal__current-value">
            {{ formatMoney(currentAmount) }}<span class="deduction-fix-modal__auto-unit">원</span>
          </span>
        </div>

        <section v-if="showAppliedAmount" class="deduction-fix-modal__auto deduction-fix-modal__auto--applied">
          <div class="deduction-fix-modal__auto-head">
            <span class="deduction-fix-modal__auto-label">자동계산 금액</span>
            <span class="deduction-fix-modal__auto-value">
              {{ formatMoney(appliedAmount) }}<span class="deduction-fix-modal__auto-unit">원</span>
            </span>
          </div>
          <p v-if="appliedNotes" class="deduction-fix-modal__auto-notes">{{ appliedNotes }}</p>
          <button
            type="button"
            class="deduction-fix-modal__auto-btn deduction-fix-modal__auto-btn--ghost"
            @click="emit('apply-applied')"
          >
            자동계산 금액 사용
          </button>
        </section>

        <section v-if="hasAutoPreview" class="deduction-fix-modal__auto">
          <div class="deduction-fix-modal__auto-head">
            <span class="deduction-fix-modal__auto-label">{{ autoLabel }}</span>
            <span class="deduction-fix-modal__auto-value">
              {{ formatMoney(autoAmount) }}<span class="deduction-fix-modal__auto-unit">원</span>
            </span>
          </div>
          <p v-if="autoHint" class="deduction-fix-modal__auto-notes">{{ autoHint }}</p>
          <div v-if="formula" class="deduction-fix-modal__auto-formula">{{ formula }}</div>
          <p v-if="notes" class="deduction-fix-modal__auto-notes">{{ notes }}</p>
          <button
            type="button"
            class="deduction-fix-modal__auto-btn"
            @click="emit('apply-auto')"
          >
            {{ autoButtonLabel }}
          </button>
        </section>

        <section v-else class="deduction-fix-modal__no-auto">
          <p class="deduction-fix-modal__no-auto-text">{{ manualOnlyMessage }}</p>
        </section>

        <section class="deduction-fix-modal__manual">
          <label class="deduction-fix-modal__manual-label" :for="inputId">{{ manualLabel }}</label>
          <div class="deduction-fix-modal__manual-row">
            <input
              :id="inputId"
              :value="manualDisplay"
              type="text"
              class="deduction-fix-modal__manual-input"
              inputmode="numeric"
              placeholder="0"
              @input="emit('manual-input', $event)"
              @keydown.enter="emit('apply-manual')"
            />
            <span class="deduction-fix-modal__manual-unit">원</span>
          </div>
        </section>
      </div>

      <footer class="deduction-fix-modal__footer">
        <v-btn variant="text" class="btn-cancel" size="small" @click="emit('cancel')">취소</v-btn>
        <v-btn variant="flat" class="btn-confirm" size="small" @click="emit('apply-manual')">적용</v-btn>
      </footer>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    title: string;
    fieldDescription?: string;
    hasAutoPreview: boolean;
    autoAmount: number;
    autoLabel?: string;
    autoButtonLabel?: string;
    autoHint?: string;
    /** 조건 반영 자동계산. 요율 기준과 다를 때만 넘긴다. */
    appliedAmount?: number | null;
    appliedNotes?: string;
    formula?: string;
    notes?: string;
    currentAmount?: number;
    hasCurrentAmount?: boolean;
    manualOnlyMessage?: string;
    manualLabel: string;
    manualDisplay: string;
    inputId: string;
    formatMoney: (n: number) => string;
  }>(),
  {
    fieldDescription: '',
    autoLabel: '요율 기준 산출액',
    autoButtonLabel: '요율 기준 산출액 적용',
    autoHint: '',
    appliedAmount: null,
    appliedNotes: '',
    formula: '',
    notes: '',
    currentAmount: 0,
    hasCurrentAmount: true,
    manualOnlyMessage: '직접 입력해 주세요.',
  }
);

const showAppliedAmount = computed(() => props.appliedAmount != null);

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  cancel: [];
  'apply-auto': [];
  'apply-applied': [];
  'apply-manual': [];
  'manual-input': [event: Event];
}>();
</script>
